import React from 'react';
import { cn } from '@rocco/utils/cn';
import { ParticipantAvatar } from './ParticipantAvatar';

type ParticipantAvatarProps = React.ComponentProps<typeof ParticipantAvatar>;

/**
 * One or more avatars for participants in a conversation. The avatars are placed
 * side by side in a row. Can be used to represent group chats.
 */
export function ParticipantAvatars({
    className,
    avatarClassName,
    imageClassName,
    fallbackClassName,
    extraClassName,
    participants,
    maxAvatars = 7,
}: {
    className?: string;
    avatarClassName?: string;
    imageClassName?: string;
    fallbackClassName?: string;
    extraClassName?: string;
    participants: NonNullable<ParticipantAvatarProps['participant']>[];
    maxAvatars?: number;
}) {
    const slicedParticipants = participants.slice(
        0,
        participants.length <= maxAvatars ? maxAvatars : maxAvatars - 1
    );
    const extraParticipants = Math.max(
        participants.length - slicedParticipants.length,
        0
    );
    const avatarCount = slicedParticipants.length + (extraParticipants ? 1 : 0);

    return maxAvatars <= 1 || participants.length <= 1 ? (
        <ParticipantAvatar
            className={avatarClassName}
            imageClassName={imageClassName}
            fallbackClassName={fallbackClassName}
            participant={participants[0]}
        />
    ) : (
        <div
            className={cn(
                'flex items-center',
                avatarCount > 3 ? '-space-x-6' : '-space-x-4',
                className
            )}
        >
            {slicedParticipants.map((participant) => (
                <ParticipantAvatar
                    key={participant.ID}
                    className={cn(
                        'transition-[margin] duration-100 will-change-[margin]',
                        avatarClassName
                    )}
                    imageClassName={imageClassName}
                    fallbackClassName={fallbackClassName}
                    participant={participant}
                />
            ))}
            {extraParticipants > 0 && (
                <div
                    className={cn(
                        'z-1 flex aspect-square h-full items-center justify-center rounded-full bg-black/50 backdrop-blur-sm',
                        avatarClassName,
                        extraClassName
                    )}
                >
                    <span
                        className={cn(
                            'whitespace-nowrap text-5 leading-tight text-white',
                            fallbackClassName
                        )}
                    >
                        +{extraParticipants}
                    </span>
                </div>
            )}
        </div>
    );
}
